import React, { useState, useMemo } from "react";
import axios from "axios";
import { Calculator, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { Card } from "./ui/card";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Slider } from "./ui/slider";
import { Button } from "./ui/button";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Estimated savings rate scales with team size (partner discounts 15%-40%).
const rateForUsers = (users) => {
  if (users <= 10) return 0.15;
  if (users <= 50) return 0.22;
  if (users <= 200) return 0.3;
  return 0.38;
};

const money = (n) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const TemEstimator = () => {
  const { toast } = useToast();
  const [users, setUsers] = useState(50);
  const [bill, setBill] = useState(3500);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const result = useMemo(() => {
    const rate = rateForUsers(users);
    const monthlySavings = Math.max(0, bill * rate);
    return {
      rate,
      monthly: monthlySavings,
      annual: monthlySavings * 12,
      newBill: Math.max(0, bill - monthlySavings),
    };
  }, [users, bill]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const valid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);
    if (!valid) {
      toast({ title: "Enter a valid email", description: "We'll send your estimate and follow up with exact numbers." });
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/estimates`, {
        email,
        users,
        monthly_bill: bill,
        monthly_savings: Math.round(result.monthly),
        annual_savings: Math.round(result.annual),
        rate_pct: Math.round(result.rate * 100),
      });
      setSent(true);
      toast({
        title: "Estimate sent!",
        description: "Check your inbox - an advisor will follow up with exact numbers.",
      });
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again, or call +1 919-296-3622." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="p-6 sm:p-8 rounded-3xl shadow-xl border-slate-100 bg-white">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-11 w-11 rounded-xl brand-gradient flex items-center justify-center">
          <Calculator className="h-5 w-5 text-white" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-slate-900">Savings Estimator</h3>
          <p className="text-sm text-slate-500">See your potential monthly & annual savings.</p>
        </div>
      </div>

      <div className="space-y-7">
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label>Number of users</Label>
            <span className="font-bold text-blue-700">{users}</span>
          </div>
          <Slider
            value={[users]}
            min={1}
            max={500}
            step={1}
            onValueChange={(v) => setUsers(v[0])}
            className="py-1"
          />
          <div className="flex justify-between text-xs text-slate-400 mt-1">
            <span>1</span>
            <span>500+</span>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="bill">Current monthly telecom/VoIP bill (USD)</Label>
          <Input
            id="bill"
            type="number"
            min={0}
            value={bill}
            onChange={(e) => setBill(Math.max(0, Number(e.target.value) || 0))}
          />
        </div>
      </div>

      <div className="mt-8 grid grid-cols-2 gap-4">
        <div className="rounded-2xl brand-gradient-soft p-5 text-center">
          <div className="text-3xl font-extrabold text-blue-700">{money(result.monthly)}</div>
          <div className="text-xs text-slate-600 mt-1">Estimated monthly savings</div>
        </div>
        <div className="rounded-2xl brand-gradient-soft p-5 text-center">
          <div className="text-3xl font-extrabold text-blue-700">{money(result.annual)}</div>
          <div className="text-xs text-slate-600 mt-1">Estimated annual savings</div>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between text-sm text-slate-600 bg-slate-50 rounded-xl px-4 py-3">
        <span>Projected new monthly bill</span>
        <span className="font-semibold text-slate-900">
          {money(result.newBill)}{" "}
          <span className="text-blue-700">({Math.round(result.rate * 100)}% off)</span>
        </span>
      </div>

      {sent ? (
        <div className="mt-6 rounded-2xl bg-green-50 border border-green-100 p-5 text-center">
          <CheckCircle2 className="h-7 w-7 text-green-600 mx-auto mb-2" />
          <p className="font-semibold text-slate-900">Your estimate is on its way!</p>
          <p className="text-sm text-slate-600 mt-1">
            Check your inbox - a technology advisor will follow up with exact numbers.
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="mt-6 space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="est-email">Email my estimate to</Label>
            <Input
              id="est-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
            />
          </div>
          <Button type="submit" disabled={loading} className="w-full brand-gradient text-white rounded-full h-12 font-semibold">
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>Email me my estimate & exact numbers <ArrowRight className="h-4 w-4 ml-2" /></>
            )}
          </Button>
        </form>
      )}
      <p className="text-xs text-slate-400 mt-3 text-center">
        Estimate only. Actual savings depend on your plans, contracts, and usage.
      </p>
    </Card>
  );
};

export default TemEstimator;
